import { parseAmountToCents, type Cents } from './money'

/**
 * "Metro 1289,70 vrijdag" as one line.
 *
 * The form underneath works and is not going away — this is a shortcut, not a
 * replacement, and it must be **wrong loudly rather than quietly**. Anything it
 * cannot read stays in the description, and whatever it did read is shown back
 * before anything is saved. A parser that silently invents a date is worse than
 * no parser at all.
 *
 * Deterministic on purpose. Rule 16 draws the line at amounts and dates: those
 * come from validators, never from a model. This is a handful of regexes over
 * Dutch, which is both cheaper and — more to the point — predictable enough to
 * write down what it will do.
 *
 * The order matters and is the subtle part. Dates are taken out *first*,
 * because "15/3" and "15,30" both look like numbers and only one of them is
 * money.
 */

export interface Phrase {
  description: string
  amountCents: Cents | null
  /** ISO date, or null when the words carried none. */
  date: string | null
  /** What the words for the date were, so the app can show what it understood. */
  dateText: string | null
  amountText: string | null
}

const WEEKDAYS: Record<string, number> = {
  maandag: 1, ma: 1,
  dinsdag: 2, di: 2,
  woensdag: 3, wo: 3,
  donderdag: 4, do: 4,
  vrijdag: 5, vr: 5,
  zaterdag: 6, za: 6,
  zondag: 0, zo: 0,
}

const MONTHS: Record<string, number> = {
  januari: 1, jan: 1, februari: 2, feb: 2, maart: 3, mrt: 3, april: 4, apr: 4,
  mei: 5, juni: 6, jun: 6, juli: 7, jul: 7, augustus: 8, aug: 8,
  september: 9, sep: 9, sept: 9, oktober: 10, okt: 10, november: 11, nov: 11,
  december: 12, dec: 12,
}

export function parsePhrase(input: string, today = new Date()): Phrase {
  let rest = ` ${input.trim()} `
  const found = takeDate(rest, today)
  if (found) rest = rest.replace(found.matched, ' ')

  const amount = takeAmount(rest)
  if (amount) rest = rest.replace(amount.matched, ' ')

  return {
    description: rest.replace(/\s+/g, ' ').trim(),
    amountCents: amount?.cents ?? null,
    amountText: amount?.matched.trim() ?? null,
    date: found?.iso ?? null,
    dateText: found?.matched.trim() ?? null,
  }
}

interface Hit {
  matched: string
  iso: string
}

function takeDate(text: string, today: Date): Hit | null {
  const lower = text.toLowerCase()

  // Explicit day and month first: the most specific reading wins, so that
  // "15 maart" is never read as "15" plus a stray word.
  const dayMonth = /\s(\d{1,2})[\s/-](\d{1,2}|[a-z]+)(?:[\s/-](\d{2,4}))?\s/.exec(lower)
  if (dayMonth) {
    const day = Number(dayMonth[1])
    const monthWord = dayMonth[2]!
    const month = /^\d+$/.test(monthWord) ? Number(monthWord) : MONTHS[monthWord]
    if (month && isRealDate(day, month)) {
      const year = dayMonth[3]
        ? Number(dayMonth[3].length === 2 ? `20${dayMonth[3]}` : dayMonth[3])
        : yearFor(day, month, today)
      return { matched: dayMonth[0], iso: iso(year, month, day) }
    }
  }

  for (const [word, delta] of [['vandaag', 0], ['morgen', 1], ['overmorgen', 2]] as const) {
    const m = new RegExp(`\\s${word}\\s`).exec(lower)
    if (m) return { matched: m[0], iso: isoFrom(today, delta) }
  }

  const inDays = /\sover\s(\d{1,3})\s(dagen|dag|weken|week)\s/.exec(lower)
  if (inDays) {
    const n = Number(inDays[1])
    // Dutch, not string surgery: the plural of "week" is "weken" — w-e-k-e-n
    // against w-e-e-k. `startsWith('week')` looks obviously right and is false,
    // which is why "over 2 weken" quietly meant two days until a test said so.
    const weeks = inDays[2] === 'week' || inDays[2] === 'weken'
    return { matched: inDays[0], iso: isoFrom(today, weeks ? n * 7 : n) }
  }

  // "volgende week maandag" before plain "maandag", or the weekday would be
  // taken first and the word "volgende" left sitting in the description.
  const nextWeekday = /\svolgende week\s([a-z]+)\s/.exec(lower)
  if (nextWeekday && WEEKDAYS[nextWeekday[1]!] !== undefined) {
    return {
      matched: nextWeekday[0],
      iso: isoFrom(today, daysUntil(today, WEEKDAYS[nextWeekday[1]!]!) + 7),
    }
  }
  const nextWeek = /\svolgende week\s/.exec(lower)
  if (nextWeek) return { matched: nextWeek[0], iso: isoFrom(today, 7) }

  for (const [word, dow] of Object.entries(WEEKDAYS)) {
    const m = new RegExp(`\\s${word}\\s`).exec(lower)
    // A bare weekday always means the next one — "vrijdag" said on a Friday is
    // next Friday, not today. Saying "today" would be a guess about intent
    // where the other reading is at least never surprising.
    if (m) return { matched: m[0], iso: isoFrom(today, daysUntil(today, dow) || 7) }
  }

  const endOfMonth = /\s(einde maand|eind van de maand|einde van de maand)\s/.exec(lower)
  if (endOfMonth) {
    const last = new Date(today.getFullYear(), today.getMonth() + 1, 0)
    return { matched: endOfMonth[0], iso: iso(last.getFullYear(), last.getMonth() + 1, last.getDate()) }
  }

  return null
}

function takeAmount(text: string): { matched: string; cents: Cents } | null {
  // Anything that looks like money, longest first so "1.289,70" beats "1".
  const candidates = [...text.matchAll(/\s(€\s?)?(\d[\d.,]*)(\s?(?:euro|eur|€))?\s/gi)]
  let best: { matched: string; cents: Cents } | null = null

  for (const m of candidates) {
    // Only the digits go to the money parser. It strips "EUR" but not "euro",
    // so " 12 euro " reached it as "12o" and came back null — the currency word
    // that made the amount unambiguous was the thing that broke it.
    const cents = parseAmountToCents(m[2]!)
    if (cents === null || cents <= 0) continue
    // A bare integer under a hundred is far more often a quantity or a house
    // number than an amount — "2 broden", "Metro 4". With a currency marker or
    // a decimal it is money and there is nothing to guess about.
    const explicit = Boolean(m[1] || m[3]) || /[.,]/.test(m[2]!)
    if (!explicit && cents < 10000) continue
    if (!best || cents > best.cents) best = { matched: m[0], cents }
  }
  return best
}

/** February the 31st is a typing mistake, not a date. */
function isRealDate(day: number, month: number): boolean {
  if (month < 1 || month > 12 || day < 1) return false
  return day <= [31, 29, 31, 30, 31, 30, 31, 31, 30, 31, 30, 31][month - 1]!
}

/**
 * A day and month with no year means the coming one.
 *
 * "3 januari" said in December is next January. Reading it as this year would
 * put a bill eleven months in the past, where nothing will ever look for it.
 */
function yearFor(day: number, month: number, today: Date): number {
  const thisYear = new Date(today.getFullYear(), month - 1, day)
  const start = new Date(today.getFullYear(), today.getMonth(), today.getDate())
  return thisYear < start ? today.getFullYear() + 1 : today.getFullYear()
}

function daysUntil(today: Date, dow: number): number {
  return (dow - today.getDay() + 7) % 7
}

function isoFrom(today: Date, days: number): string {
  const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() + days)
  return iso(d.getFullYear(), d.getMonth() + 1, d.getDate())
}

function iso(y: number, m: number, d: number): string {
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`
}
